// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Fetch upcoming events
export function useEvents() {
  return useQuery({
    queryKey: ["events-for-checklists"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, start_date, end_date")
        .gte("end_date", new Date().toISOString().split("T")[0])
        .order("start_date", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
}

// Fetch all events (for overview)
export function useAllUpcomingEvents() {
  return useQuery({
    queryKey: ["all-upcoming-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, start_date, end_date")
        .gte("end_date", new Date().toISOString().split("T")[0])
        .order("start_date", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
}

// Fetch event staff
export function useEventStaff() {
  return useQuery({
    queryKey: ["event-staff-checklists"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_staff")
        .select("id, name, email")
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });
}

// Checklist tasks for an event
export function useChecklistTasks(eventId: string | null) {
  return useQuery({
    queryKey: ["checklist-tasks", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manage_event_checklist")
        .select("*")
        .eq("event_id", eventId!)
        .order("category")
        .order("sort_order")
        .order("priority");
      if (error) throw error;
      return data || [];
    },
    enabled: !!eventId,
  });
}

// Checklist progress view
export function useChecklistProgress(eventId: string | null) {
  return useQuery({
    queryKey: ["checklist-progress", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manage_checklist_progress")
        .select("*")
        .eq("event_id", eventId!);
      if (error) throw error;
      return data || [];
    },
    enabled: !!eventId,
  });
}

// All checklist progress (for overview dashboard)
export function useAllChecklistProgress() {
  return useQuery({
    queryKey: ["all-checklist-progress"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manage_checklist_progress")
        .select("*");
      if (error) throw error;
      return data || [];
    },
  });
}

// Templates
export function useTemplates() {
  return useQuery({
    queryKey: ["checklist-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manage_checklist_templates")
        .select("*")
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });
}

// Template items
export function useTemplateItems(templateId: string | null) {
  return useQuery({
    queryKey: ["checklist-template-items", templateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manage_checklist_template_items")
        .select("*")
        .eq("template_id", templateId!)
        .order("category")
        .order("sort_order");
      if (error) throw error;
      return data || [];
    },
    enabled: !!templateId,
  });
}

// Event notes
export function useEventNotes(eventId: string | null) {
  return useQuery({
    queryKey: ["event-notes", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manage_event_notes")
        .select("*")
        .eq("event_id", eventId!)
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!eventId,
  });
}

// Mutations
export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase
        .from("manage_event_checklist")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-tasks"] });
      qc.invalidateQueries({ queryKey: ["checklist-progress"] });
      qc.invalidateQueries({ queryKey: ["all-checklist-progress"] });
    },
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (task: Record<string, any>) => {
      const { error } = await supabase.from("manage_event_checklist").insert(task);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-tasks"] });
      qc.invalidateQueries({ queryKey: ["checklist-progress"] });
      toast.success("Task added");
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("manage_event_checklist").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-tasks"] });
      qc.invalidateQueries({ queryKey: ["checklist-progress"] });
      toast.success("Task deleted");
    },
  });
}

export function useGenerateFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId, templateId }: { eventId: string; templateId: string }) => {
      const { data, error } = await supabase.rpc("manage_generate_checklist_from_template", {
        p_event_id: eventId,
        p_template_id: templateId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ["checklist-tasks"] });
      qc.invalidateQueries({ queryKey: ["checklist-progress"] });
      toast.success(`Generated ${count} tasks from template`);
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to generate checklist");
    },
  });
}

// Template CRUD
export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (t: Record<string, any>) => {
      const { data, error } = await supabase.from("manage_checklist_templates").insert(t).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-templates"] });
      toast.success("Template created");
    },
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from("manage_checklist_templates").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-templates"] });
      toast.success("Template updated");
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("manage_checklist_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-templates"] });
      toast.success("Template deleted");
    },
  });
}

// Template items CRUD
export function useCreateTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (item: Record<string, any>) => {
      const { error } = await supabase.from("manage_checklist_template_items").insert(item);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-template-items"] });
      toast.success("Item added");
    },
  });
}

export function useUpdateTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from("manage_checklist_template_items").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-template-items"] });
    },
  });
}

export function useDeleteTemplateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("manage_checklist_template_items").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-template-items"] });
      toast.success("Item deleted");
    },
  });
}

// Notes CRUD
export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (note: Record<string, any>) => {
      const { error } = await supabase.from("manage_event_notes").insert(note);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-notes"] });
      toast.success("Note added");
    },
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from("manage_event_notes").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-notes"] });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("manage_event_notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-notes"] });
      toast.success("Note deleted");
    },
  });
}

// Create template from event checklist
export function useCreateTemplateFromEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId, name, description }: { eventId: string; name: string; description: string }) => {
      // First create the template
      const { data: template, error: tErr } = await supabase
        .from("manage_checklist_templates")
        .insert({ name, description, is_default: false })
        .select()
        .single();
      if (tErr) throw tErr;

      // Get all tasks from the event
      const { data: tasks, error: taskErr } = await supabase
        .from("manage_event_checklist")
        .select("category, task, priority, sort_order, notes")
        .eq("event_id", eventId);
      if (taskErr) throw taskErr;

      if (tasks && tasks.length > 0) {
        const items = tasks.map((t: any) => ({
          template_id: template.id,
          category: t.category,
          task: t.task,
          priority: t.priority,
          days_before_event: 7,
          sort_order: t.sort_order,
          notes: t.notes,
        }));
        const { error: iErr } = await supabase.from("manage_checklist_template_items").insert(items);
        if (iErr) throw iErr;
      }

      return template;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist-templates"] });
      toast.success("Template created from event checklist");
    },
  });
}

// Category helpers
export const CATEGORIES = [
  "signage", "staffing", "travel", "officials", "scorekeepers",
  "venue_prep", "marketing", "registration", "hospitality", "other"
] as const;

export const CATEGORY_COLORS: Record<string, string> = {
  signage: "bg-blue-100 text-blue-800 border-blue-200",
  staffing: "bg-purple-100 text-purple-800 border-purple-200",
  travel: "bg-sky-100 text-sky-800 border-sky-200",
  officials: "bg-amber-100 text-amber-800 border-amber-200",
  scorekeepers: "bg-emerald-100 text-emerald-800 border-emerald-200",
  venue_prep: "bg-slate-100 text-slate-800 border-slate-200",
  marketing: "bg-pink-100 text-pink-800 border-pink-200",
  registration: "bg-indigo-100 text-indigo-800 border-indigo-200",
  hospitality: "bg-orange-100 text-orange-800 border-orange-200",
  other: "bg-gray-100 text-gray-800 border-gray-200",
  general: "bg-gray-100 text-gray-800 border-gray-200",
};

export const CATEGORY_PROGRESS_COLORS: Record<string, string> = {
  signage: "bg-blue-500",
  staffing: "bg-purple-500",
  travel: "bg-sky-500",
  officials: "bg-amber-500",
  scorekeepers: "bg-emerald-500",
  venue_prep: "bg-slate-500",
  marketing: "bg-pink-500",
  registration: "bg-indigo-500",
  hospitality: "bg-orange-500",
  other: "bg-gray-500",
};

export const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-100 text-red-800 border-red-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  medium: "bg-blue-100 text-blue-800 border-blue-300",
  low: "bg-gray-100 text-gray-600 border-gray-300",
};

export const STATUS_COLORS: Record<string, string> = {
  todo: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  blocked: "bg-red-100 text-red-700",
  done: "bg-green-100 text-green-700",
};

export function formatCategory(cat: string) {
  return cat.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
